import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BuildingLibraryIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  LockClosedIcon,
  EnvelopeIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { paymentConfigApi, Bank, AccountType, PaymentConfig } from '@/api/paymentConfigApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/Button'

type ModalStep = 'bank-details' | 'otp-verification'

export default function PaymentConfigPage() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [modalStep, setModalStep] = useState<ModalStep>('bank-details')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [otpExpiresIn, setOtpExpiresIn] = useState(0)
  const [bankSearch, setBankSearch] = useState('')
  const [showBankDropdown, setShowBankDropdown] = useState(false)
  const [currentConfigId, setCurrentConfigId] = useState<string | null>(null)
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    bankCode: '',
    bankName: '',
    accountNumber: '',
    accountType: 'wallet' as AccountType,
  })

  // OTP state
  const [otp, setOtp] = useState(['', '', '', '', '', ''])

  // Countdown timer
  useEffect(() => {
    if (otpExpiresIn > 0 && modalStep === 'otp-verification') {
      const timer = setInterval(() => {
        setOtpExpiresIn((prev) => Math.max(0, prev - 1))
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [otpExpiresIn, modalStep])

  // Fetch current config
  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['paymentConfig'],
    queryFn: () => paymentConfigApi.getPaymentConfig(),
  })

  // Fetch banks list
  const { data: banksData, isLoading: banksLoading } = useQuery({
    queryKey: ['banks'],
    queryFn: () => paymentConfigApi.getBanks(),
  })

  // Mutations
  const initiateMutation = useMutation({
    mutationFn: paymentConfigApi.initiatePaymentConfig,
    onSuccess: (data) => {
      setCurrentConfigId(data.data.configId)
      setMaskedEmail(data.data.email)
      setOtpExpiresIn(data.data.expiresIn * 60)
      setModalStep('otp-verification')
    },
  })

  const verifyMutation = useMutation({
    mutationFn: ({ configId, otp }: { configId: string; otp: string }) =>
      paymentConfigApi.verifyPaymentConfig(configId, otp),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfig'] })
      closeModal()
    },
  })

  const resendMutation = useMutation({
    mutationFn: (configId: string) => paymentConfigApi.resendOTP(configId),
    onSuccess: (data) => {
      setMaskedEmail(data.data.email)
      setOtpExpiresIn(data.data.expiresIn * 60)
      setOtp(['', '', '', '', '', ''])
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (configId: string) => paymentConfigApi.deactivatePaymentConfig(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfig'] })
    },
  })

  // Get configs from API response
  const configs: PaymentConfig[] = configData?.data?.configs || []

  const banks = banksData?.data || []
  const isLoading = configLoading || banksLoading

  // Check which account types already have active configs
  const walletConfig = configData?.data?.walletConfig
  const invoiceConfig = configData?.data?.invoiceConfig
  const hasWallet = !!walletConfig
  const hasInvoice = !!invoiceConfig
  const canAddMore = !hasWallet || !hasInvoice

  const filteredBanks = banks.filter((bank: Bank) =>
    bank.name.toLowerCase().includes(bankSearch.toLowerCase())
  )

  // Modal handlers
  const openModal = (configToEdit?: PaymentConfig) => {
    if (configToEdit) {
      // Editing existing config
      setEditingConfigId(configToEdit.id || null)
      setFormData({
        bankCode: configToEdit.bankAccount.bankCode,
        bankName: configToEdit.bankAccount.bankName,
        accountNumber: configToEdit.bankAccount.accountNumber,
        accountType: configToEdit.accountType || 'wallet',
      })
      setBankSearch(configToEdit.bankAccount.bankName)
    } else {
      // Adding new config - default to type that doesn't exist yet
      setEditingConfigId(null)
      const defaultType: AccountType = !hasWallet ? 'wallet' : 'invoice'
      setFormData({ bankCode: '', bankName: '', accountNumber: '', accountType: defaultType })
      setBankSearch('')
    }
    setCurrentConfigId(null)
    setModalStep('bank-details')
    setOtp(['', '', '', '', '', ''])
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setModalStep('bank-details')
    setOtp(['', '', '', '', '', ''])
    setEditingConfigId(null)
    setCurrentConfigId(null)
    initiateMutation.reset()
    verifyMutation.reset()
  }

  // Form handlers
  const handleBankDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    initiateMutation.mutate(formData)
  }

  const handleSelectBank = (bank: Bank) => {
    setFormData((prev) => ({
      ...prev,
      bankCode: bank.code || bank.name,
      bankName: bank.name,
    }))
    setBankSearch(bank.name)
    setShowBankDropdown(false)
  }

  // OTP handlers
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1 || !/^\d*$/.test(value)) return
    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return
    setOtp(pastedData.split('').concat(Array(6).fill('')).slice(0, 6))
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus()
    }
  }

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault()
    const otpString = otp.join('')
    if (otpString.length === 6 && currentConfigId) {
      verifyMutation.mutate({ configId: currentConfigId, otp: otpString })
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getStatusBadge = (status: string, isActive: boolean) => {
    if (status === 'VERIFIED' && isActive) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
    } else if (status === 'PENDING') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Configs</p>
              <p className="text-2xl font-bold text-gray-900">{configs.length}</p>
            </div>
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <CreditCardIcon className="h-5 w-5 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {configs.filter(c => c.status === 'VERIFIED' && c.isActive).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Wallet</p>
              <p className={`text-lg font-bold ${hasWallet ? 'text-blue-600' : 'text-gray-400'}`}>
                {hasWallet ? 'Configured' : 'Not Setup'}
              </p>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              hasWallet ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              <BanknotesIcon className={`h-5 w-5 ${hasWallet ? 'text-blue-600' : 'text-gray-400'}`} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Invoice</p>
              <p className={`text-lg font-bold ${hasInvoice ? 'text-purple-600' : 'text-gray-400'}`}>
                {hasInvoice ? 'Configured' : 'Not Setup'}
              </p>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              hasInvoice ? 'bg-purple-100' : 'bg-gray-100'
            }`}>
              <BuildingLibraryIcon className={`h-5 w-5 ${hasInvoice ? 'text-purple-600' : 'text-gray-400'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
        <h2 className="text-lg font-semibold text-gray-900">Payment Configuration</h2>
        <Button
          onClick={() => openModal()}
          variant="primary"
          size="sm"
          disabled={!canAddMore}
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Account
        </Button>
      </div>

      {/* Configuration Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.length > 0 ? (
              configs.map((config) => (
                <tr key={config.id || config.accountType} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <BuildingLibraryIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{config.bankAccount.bankName}</div>
                        <div className="text-sm text-gray-500">{config.bankAccount.bankCode}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono text-gray-900">{config.bankAccount.accountNumber}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      config.accountType === 'invoice'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {config.accountType === 'invoice' ? 'Invoice' : 'Wallet'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(config.status, config.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {config.verifiedAt ? new Date(config.verifiedAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openModal(config)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      {config.status === 'VERIFIED' && config.isActive && (
                        <button
                          onClick={() => {
                            if (confirm('Deactivate this payment configuration?')) {
                              deactivateMutation.mutate(config.id)
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          disabled={deactivateMutation.isPending}
                        >
                          {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No payment configuration</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by setting up your bank account.</p>
                  <div className="mt-4">
                    <Button onClick={() => openModal()} variant="primary" size="sm">
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Account
                    </Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {modalStep === 'bank-details'
                      ? editingConfigId ? 'Edit Bank Account' : 'Add Bank Account'
                      : 'Verify OTP'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Step {modalStep === 'bank-details' ? '1' : '2'} of 2
                  </p>
                </div>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              {modalStep === 'bank-details' ? (
                <form onSubmit={handleBankDetailsSubmit} className="p-6 space-y-4">
                  {/* Bank Selection */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={bankSearch}
                        onChange={(e) => {
                          setBankSearch(e.target.value)
                          setShowBankDropdown(true)
                          if (!e.target.value) {
                            setFormData((prev) => ({ ...prev, bankCode: '', bankName: '' }))
                          }
                        }}
                        onFocus={() => setShowBankDropdown(true)}
                        placeholder="Search bank..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    {showBankDropdown && filteredBanks.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filteredBanks.map((bank: Bank) => (
                          <button
                            key={bank.code || bank.name}
                            type="button"
                            onClick={() => handleSelectBank(bank)}
                            className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm ${
                              formData.bankName === bank.name ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                            }`}
                          >
                            {bank.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Account Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData((prev) => ({ ...prev, accountNumber: e.target.value }))}
                      placeholder="Enter account number"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Account Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => !editingConfigId && !hasWallet && setFormData((prev) => ({ ...prev, accountType: 'wallet' }))}
                        disabled={editingConfigId ? formData.accountType !== 'wallet' : hasWallet}
                        className={`p-3 border-2 rounded-lg text-left transition-all ${
                          formData.accountType === 'wallet'
                            ? 'border-blue-500 bg-blue-50'
                            : (editingConfigId ? formData.accountType !== 'wallet' : hasWallet)
                              ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            formData.accountType === 'wallet' ? 'border-blue-500' : 'border-gray-300'
                          }`}>
                            {formData.accountType === 'wallet' && (
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            formData.accountType === 'wallet' ? 'text-blue-700' : 'text-gray-700'
                          }`}>
                            Wallet
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          {!editingConfigId && hasWallet ? 'Already configured' : 'Parents top-up wallet balance'}
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => !editingConfigId && !hasInvoice && setFormData((prev) => ({ ...prev, accountType: 'invoice' }))}
                        disabled={editingConfigId ? formData.accountType !== 'invoice' : hasInvoice}
                        className={`p-3 border-2 rounded-lg text-left transition-all ${
                          formData.accountType === 'invoice'
                            ? 'border-blue-500 bg-blue-50'
                            : (editingConfigId ? formData.accountType !== 'invoice' : hasInvoice)
                              ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            formData.accountType === 'invoice' ? 'border-blue-500' : 'border-gray-300'
                          }`}>
                            {formData.accountType === 'invoice' && (
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            formData.accountType === 'invoice' ? 'text-blue-700' : 'text-gray-700'
                          }`}>
                            Invoice
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          {!editingConfigId && hasInvoice ? 'Already configured' : 'Pay via generated invoices'}
                        </p>
                      </button>
                    </div>
                  </div>

                  {initiateMutation.isError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">
                        {(initiateMutation.error as any)?.response?.data?.message || 'Failed to submit'}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={initiateMutation.isPending}
                      disabled={!formData.bankName || !formData.accountNumber}
                    >
                      Continue
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="p-6">
                  <div className="text-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShieldCheckIcon className="w-7 h-7 text-blue-600" />
                    </div>

                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Enter Verification Code</h2>
                    <p className="text-sm text-gray-500 mb-6">
                      Sent to <span className="font-medium">{maskedEmail}</span>
                    </p>

                    <div className="flex justify-center space-x-2 mb-4">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={index === 0 ? handleOtpPaste : undefined}
                          className="w-11 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ))}
                    </div>

                    {otpExpiresIn > 0 ? (
                      <p className="text-sm text-gray-500 mb-4">
                        Expires in <span className="font-medium text-blue-600">{formatTime(otpExpiresIn)}</span>
                      </p>
                    ) : (
                      <p className="text-sm text-red-600 mb-4">Code expired</p>
                    )}

                    {verifyMutation.isError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-red-700">
                          {(verifyMutation.error as any)?.response?.data?.message || 'Invalid code'}
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-full"
                        loading={verifyMutation.isPending}
                        disabled={otp.join('').length !== 6 || otpExpiresIn === 0}
                      >
                        <LockClosedIcon className="w-4 h-4 mr-2" />
                        Verify
                      </Button>

                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full"
                        onClick={() => currentConfigId && resendMutation.mutate(currentConfigId)}
                        loading={resendMutation.isPending}
                        disabled={!currentConfigId}
                      >
                        <EnvelopeIcon className="w-4 h-4 mr-2" />
                        Resend Code
                      </Button>

                      <button
                        type="button"
                        onClick={() => { setModalStep('bank-details'); setOtp(['', '', '', '', '', '']) }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Back to bank details
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Security Info */}
      <div className="bg-gray-50 border rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <ShieldCheckIcon className="w-5 h-5 text-gray-400" />
          <p className="text-sm text-gray-600">
            Bank details are encrypted. OTP verification ensures only authorized users can modify settings.
          </p>
        </div>
      </div>
    </div>
  )
}
